import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, CardActionArea, Button, useTheme, Avatar, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { motion } from 'framer-motion';

const ProjectCard = ({ project, dragHandleProps, isAdmin }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [responsibleImage, setResponsibleImage] = useState('');
  
  // Log para verificar os dados do projeto
  console.log('Dados do projeto no ProjectCard:', project);
  
  // Buscar a imagem do responsável, se o projeto tiver um responsável
  useEffect(() => {
    const fetchResponsibleImage = async () => {
      if (project.responsible) {
        try {
          const response = await api.get(`/technical-responsibles/by-name/${project.responsible}`);
          if (response.data && response.data.profile_image_url) {
            setResponsibleImage(response.data.profile_image_url);
          }
        } catch (error) {
          console.error('Erro ao buscar imagem do responsável:', error);
        }
      }
    };
    
    fetchResponsibleImage();
  }, [project.responsible]);
  
  const handleClick = () => {
    navigate(`/projects/${project.id}`);
  };
  
  // Função atualizada para navegar para a página de detalhes do responsável
  const handleResponsibleClick = async (event) => {
    event.stopPropagation(); // Impedir que o clique propague para o CardActionArea
    
    // Verifica se o responsável existe
    if (project.responsible) {
      try {
        // Buscar o ID do responsável com base no nome
        const response = await api.get(`/technical-responsibles/by-name/${project.responsible}`);
        if (response.data && response.data.responsible_id) {
          navigate(`/responsibles/${response.data.responsible_id}`);
        } else {
          // Caso não encontre o responsável específico, vai para a lista de responsáveis
          navigate('/responsibles');
        }
      } catch (error) {
        console.error('Erro ao buscar responsável:', error);
        // Se ocorrer algum erro, navega para a lista de responsáveis
        navigate('/responsibles');
      }
    }
  };
  
  // Função para obter as iniciais do nome do responsável
  const getInitials = (name) => {
    if (!name) return '';
    const names = name.split(' ');
    return names.length > 1 
      ? `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}` 
      : names[0].charAt(0);
  };
  
  return (
    <motion.div 
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      style={{ height: '100%' }}
    >
      <Card 
        sx={{ 
          minWidth: 275, 
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          border: `1px solid ${theme.palette.divider}`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <CardActionArea 
          onClick={handleClick} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%', 
            alignItems: 'flex-start',
            justifyContent: 'flex-start'
          }}
        >
          <CardContent sx={{ 
            p: { xs: 2, sm: 3 }, 
            width: '100%', 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
              {project.responsible && (
                <Avatar
                  src={responsibleImage}
                  alt={project.responsible}
                  onClick={handleResponsibleClick}
                  sx={{
                    width: 40,
                    height: 40,
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }}
                >
                  {getInitials(project.responsible)}
                </Avatar>
              )}
              
              {isAdmin && dragHandleProps && (
                <Tooltip title="Arraste para reordenar" placement="top">
                  <Box
                    {...dragHandleProps}
                    component={motion.div}
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    sx={{
                      color: 'text.secondary',
                      cursor: 'grab',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '50%',
                      display: 'flex',
                      padding: '4px',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                      },
                      '&:active': {
                        cursor: 'grabbing'
                      }
                    }}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                </Tooltip>
              )}
              
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 600, 
                  ml: 1, 
                  fontSize: '1.1rem',
                  height: '1.5em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: 'calc(100% - 100px)'
                }}
              >
                {project.name || 'Sem nome'}
              </Typography>
              
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Typography 
                  color="text.secondary"
                  sx={{
                    bgcolor: project.priority === 'Alta' 
                      ? 'error.light' 
                      : project.priority === 'Média' 
                        ? 'warning.light' 
                        : 'success.light',
                    color: project.priority === 'Alta' 
                      ? 'error.dark' 
                      : project.priority === 'Média' 
                        ? 'warning.dark' 
                        : 'success.dark',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}
                >
                  {project.priority || 'Não definida'}
                </Typography>
                
                {/* Exibir status do projeto se estiver concluído */}
                {project.status_id === 2 && (
                  <Typography 
                    color="text.secondary"
                    sx={{
                      bgcolor: 'info.light',
                      color: 'info.dark',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}
                  >
                    Concluído
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 3, 
                fontSize: '0.95rem', 
                lineHeight: 1.5,
                height: '4.5em', // Altura fixa para 3 linhas
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3, // Limita a 3 linhas
                textOverflow: 'ellipsis'
              }}
            >
              {project.description || 'Sem descrição'}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Responsável: 
              </Typography>
              {project.responsible ? (
                <Button 
                  onClick={handleResponsibleClick}
                  sx={{ 
                    textTransform: 'none', 
                    p: 0, 
                    ml: 1, 
                    minWidth: 'auto',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    color: 'primary.main',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {project.responsible}
                </Button>
              ) : (
                <Typography variant="body1" sx={{ ml: 1, fontWeight: 400 }}>
                  Não atribuído
                </Typography>
              )}
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 'auto',
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Início: {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Não definido'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Término: {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Não definido'}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </motion.div>
  );
};

export default ProjectCard; 